import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import TextField from '@material-ui/core/TextField';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Avatar from '@material-ui/core/Avatar';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import ApplicationIcon from '@material-ui/icons/SettingsApplications';
import TypeIcon from '@material-ui/icons/List';
import UsersIcon from '@material-ui/icons/People';
import UserIcon from '@material-ui/icons/Person';
import DescriptionIcon from '@material-ui/icons/Notes';
import { useStoreData } from 'grb-webui/store';
import SearchBar from 'tuc-webui-base/components/SearchBar';
import LicenseSelectHOC from '../../components/LicenseSelect';
import {
  LICENSES_ENTITY,
} from '../../Entities';
import './CreateKeyDialog.scss';

//
// CreateKeyDialogHOC
//
const CreateKeyDialogHOC = ({
  useApplicationName,
  useDescription,
  useLicense,
  makeLicenseIcon,
  create,
  getLicenseTitle = k => k.id,
  getApplicationName = k => k.appName,
  getDescription = k => k.description,
  userKey,
  admin,
  LicenseItem,
}) => {
  const userConfig = create ? create.users : undefined;
  const UserKeyIcon = userKey && userKey.icon;
  const {
    entity: userEntity,
    getUserId = u => u.id,
    getUserUsername = u => u.username,
    getUserLabel = u => u.username || u.email,
  } = userConfig || {};

  const UserListItem = ({ user, onSelect, selected }) => {
    const handleSelect = () => {
      onSelect(user);
    };

    const label = getUserLabel(user) || getUserUsername(user);
    return (
      <ListItem
        button
        onClick={handleSelect}
        selected={selected}
        id={`user-item-${getUserId(user)}`}
      >
        <ListItemAvatar>
          <Avatar alt={getUserUsername(user) || getUserLabel(user)}>
            <UserIcon />
          </Avatar>
        </ListItemAvatar>
        <ListItemText id={`user-item-label-${label}`} primary={label} />
      </ListItem>
    );
  };

  UserListItem.propTypes = {
    user: PropTypes.shape({
      username: PropTypes.string,
    }).isRequired,
    onSelect: PropTypes.func.isRequired,
    selected: PropTypes.bool.isRequired,
  };

  const UserComponent = userConfig && userConfig.Component || UserListItem;

  const LicenseSelect = LicenseSelectHOC({ admin, loadLicenses: false, LicenseItem, noLabelOnSelect: true });

  const CreateKeyDialog = (props) => {
    const { onClose, open, viewService, apiKey, store, description: d, appName: app } = props;
    const [LicenseIcon] = useState(() => viewService.getIcon('Licenses'));
    const [appName, setAppName] = useState(apiKey ? getApplicationName(apiKey) : app);
    const [description, setDescription] = useState(apiKey ? getDescription(apiKey) : d);
    const { licenses } = useStoreData(store, useLicense ? [LICENSES_ENTITY] : []);
    const [selectedLicenseId, setSelectedLicenseId] = useState(undefined);
    const [lastSelectedLicenseId, setLastSelectedLicenseId] = useState(undefined);
    const { users } = useStoreData(store, userConfig ? [userEntity] : []);
    const [userState, setUserState] = useState({ context: { search: '' } });

    useEffect(() => {
      setLastSelectedLicenseId(licenses && licenses.length ? licenses[0].id : undefined);
      setSelectedLicenseId(licenses && licenses.length ? licenses[0].id : undefined);
    }, [licenses]);

    const filterUsersAndSelect = useCallback((usrs, state) => {
      const { selectedUserId, context } = state;
      const { search } = context;
      const SEARCH = search && search.toUpperCase();
      const matchContext = !search
        ? () => true
        : (...strs) => {
          return strs.find(s => (s && (s.toUpperCase().indexOf(SEARCH) >= 0)));
        };
      const { apiKeysDisabledProp } = userConfig || {};
      const filteredUsers = usrs.filter((user) => {
        if (apiKeysDisabledProp && user[apiKeysDisabledProp]) {
          return false;
        }
        const m = matchContext(
          getUserId(user),
          getUserUsername(user),
          getUserLabel(user),
        );
        return m;
      });
      const selectedUser = selectedUserId
        && filteredUsers.find(user => getUserId(user) === selectedUserId);
      return {
        ...state,
        filteredUsers,
        selectedUserId: selectedUser
          ? getUserId(selectedUser)
          : (filteredUsers.length && getUserId(filteredUsers[0]) || null),
      };
    }, []);

    useEffect(() => {
      if (userConfig) {
        setUserState(filterUsersAndSelect(users, userState));
      }
    }, [users]);

    const handleCancel = () => {
      onClose(undefined);
    };

    const getSelectedLicenseId = () => {
      return selectedLicenseId || (userKey || !licenses || !licenses.length ? undefined : licenses[0].id);
    };

    const handleOk = () => {
      if (apiKey) {
        onClose(apiKey, {
          appName,
          description,
        });
      } else {
        onClose({
          ...(userKey && { type: selectedLicenseId ? 2 : 1 } || {}),
          ...(userConfig && { userId: userState.selectedUserId } || {}),
          licenseId: getSelectedLicenseId(),
          appName: appName || undefined,
          description: description || undefined,
        });
      }
    };

    // Filter licenses
    const handleLicenseChange = (id) => {
      if (id) {
        setLastSelectedLicenseId(id);
      }
      setSelectedLicenseId(id);
    };

    const handleChangeAppName = (evt) => {
      setAppName(evt.target.value);
    };

    const handleChangeDescription = (evt) => {
      setDescription(evt.target.value);
    };

    const handleContextChange = (context) => {
      setUserState(filterUsersAndSelect(users, { ...userState, context }));
    };

    const handleUserSelected = (user) => {
      setUserState(filterUsersAndSelect(users, { ...userState, selectedUserId: getUserId(user) }));
    };

    const handleUserKeySelect = () => {
      setSelectedLicenseId(null);
    };

    const handleLicenseKeySelect = () => {
      setSelectedLicenseId(lastSelectedLicenseId);
    };

    const canApply = !apiKey
      ? (!userConfig || !!userState.selectedUserId)
      : ((useDescription && (getDescription(apiKey) !== description))
          || (useApplicationName && (getApplicationName(apiKey) !== appName)));

    const sel = getSelectedLicenseId();

    return (
      <Dialog
        disableBackdropClick
        aria-labelledby="grb-create-key-title"
        className={classnames('grb-create-key-dialog', apiKey && 'grb-edit-key-dialog')}
        onClose={handleCancel}
        open={open}
      >
        <DialogTitle id="grb-create-key-title">{apiKey ? 'Edit API Key' : 'Create an API Key'}</DialogTitle>
        <DialogContent className={classnames('grb-create-key-dialog-content', !useApplicationName && 'grb-create-key-dialog-content-no-app-name')}>
          {/* Name section */}
          {useApplicationName && (
            <section className="grb-create-key-dialog-section">
              <header>
                <div className="grb-create-key-dialog-section-icon grb-create-key-dialog-section-icon-textfield">
                  <ApplicationIcon />
                </div>
                <TextField
                  id="create-key-dialog-name-field"
                  label="Enter an application name"
                  value={appName || ''}
                  onChange={handleChangeAppName}
                  margin="dense"
                  fullWidth
                />
                {/* <Typography variant="subtitle1">Select an application name</Typography> */}
              </header>
            </section>
          )}
          {/* Description section */}
          {useDescription && (
            <section className="grb-create-key-dialog-section">
              <header>
                <div className="grb-create-key-dialog-section-icon grb-create-key-dialog-section-icon-textfield">
                  <DescriptionIcon />
                </div>
                <TextField
                  id="create-key-dialog-description-field"
                  label="Enter a description"
                  value={description || ''}
                  onChange={handleChangeDescription}
                  margin="dense"
                  multiline
                  rowsMax={3}
                  fullWidth
                />
              </header>
            </section>
          )}
          {/* License section */}
          {!apiKey && useLicense && !!userKey && (
            <section className="grb-create-key-dialog-section grb-create-key-dialog-section-top">
              <header>
                <div className="grb-create-key-dialog-section-icon grb-create-key-dialog-section-icon-radio">
                  <TypeIcon />
                </div>
                <div className="grb-create-key-dialog-type-group">
                  <FormControlLabel
                    control={(
                      <Radio
                        checked={!selectedLicenseId}
                        onChange={handleUserKeySelect}
                        value="userType"
                        name="grb-create-key-dialog-user-type"
                        id="grb-create-key-dialog-user-type"
                        inputProps={{ 'aria-label': 'user type' }}
                      />
                    )}
                    label={userKey.label || 'Create user key'}
                  />
                  <FormControlLabel
                    control={(
                      <Radio
                        checked={!!selectedLicenseId}
                        onChange={handleLicenseKeySelect}
                        value="userType"
                        name="grb-create-key-dialog-license-type"
                        id="grb-create-key-dialog-license-type"
                        inputProps={{ 'aria-label': 'license type' }}
                        disabled={!licenses || !licenses.length}
                      />
                    )}
                    label={'Create key for a license'}
                  />
                  <div className="grb-key-dialog-indented-block">
                    {(!!licenses && licenses.length) ? (
                      <LicenseSelect
                        {...props}
                        licenses={licenses}
                        value={selectedLicenseId || lastSelectedLicenseId}
                        onValueChange={handleLicenseChange}
                        allowAllLicenses={false}
                        size="medium"
                        autoFocus
                        disabled={!selectedLicenseId}
                      />
                    ) : (
                      <Typography className="grb-key-dialog-no-licenses">No licenses</Typography>
                    )}
                  </div>
                </div>
              </header>
              {!userKey && (
                <main>
                  <List component="nav" aria-label="key types">
                    {userKey && (
                      <ListItem
                        button
                        selected={sel === undefined}
                        onClick={() => handleLicenseChange(undefined)}
                        id="user-create-key-item"
                      >
                        <ListItemIcon>
                          <UserKeyIcon />
                        </ListItemIcon>
                        <ListItemText primary={userKey.label} />
                      </ListItem>
                    )}
                    {(licenses || []).map((l) => {
                      const Icon = makeLicenseIcon ? makeLicenseIcon(l, licenses) : LicenseIcon;
                      return (
                        <ListItem
                          button
                          selected={sel === l.id}
                          onClick={() => handleLicenseChange(l.id)}
                          key={l.id}
                          id={`create-key-item-${l.id}`}
                        >
                          <ListItemIcon>
                            <Icon />
                          </ListItemIcon>
                          <ListItemText primary={userKey ? `Key for license ${getLicenseTitle(l)}` : getLicenseTitle(l)} />
                        </ListItem>
                      );
                    })}
                  </List>
                </main>
              )}
            </section>
          )}
          {/* Users section */}
          {!apiKey && userConfig && (
            <section className="grb-create-key-dialog-section grb-create-key-dialog-users-section">
              <header>
                <div className="grb-create-key-dialog-section-icon">
                  <UsersIcon />
                </div>
                <Typography variant="subtitle1">{userConfig.usersSectionTitle || 'Select User'}</Typography>
              </header>
              <main>
                <header className="grb-tablepanel-toolbar">
                  <SearchBar
                    {...props}
                    context={userState.context}
                    onContextChange={handleContextChange}
                    placeholder="Search users..."
                    id="grb-create-key-dialog-search-user-bar"
                  />
                </header>
                {(userState.filteredUsers && userState.filteredUsers.length) ? (
                  <List component="nav" aria-label="users" id="grb-create-key-dialog-user-list" dense>
                    {(userState.filteredUsers || []).map(u => (
                      <UserComponent
                        {...props}
                        user={u}
                        key={getUserId(u)}
                        onSelect={handleUserSelected}
                        selected={getUserId(u) === userState.selectedUserId}
                      />
                    ))}
                  </List>
                ) : (
                  <Typography variant="body1" className="grb-create-key-dialog-no-users">
                    No users
                  </Typography>
                )}
              </main>
            </section>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="primary" id="cancel-create-api-key">
            Cancel
          </Button>
          <Button onClick={handleOk} color="primary" id="apply-create-api-key" disabled={!canApply}>
            {apiKey ? 'Apply' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  CreateKeyDialog.propTypes = {
    store: PropTypes.shape({}).isRequired,
    viewService: PropTypes.shape({
      getIcon: PropTypes.func,
    }).isRequired,
    apiKey: PropTypes.shape({
      description: PropTypes.string,
      appName: PropTypes.string,
    }),
    description: PropTypes.string,
    appName: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
  };

  CreateKeyDialog.defaultProps = {
    apiKey: undefined,
    description: '',
    appName: '',
  };

  return CreateKeyDialog;
};

export default CreateKeyDialogHOC;
